import type React from "react";
import { useState } from "react";
import { CalendarDays } from "lucide-react";
import type { reportingPeriod } from "@duress/shared";
import { fmtHours, fmtPct, fmtInt } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { TierBadge } from "../dashboard/TierBadge";
import {
  useDashboardView,
  useDailyActivity,
  type SelectedView,
} from "@/features/periods/usePeriods";
import { TeamActivity } from "./TeamActivity";
import { TeamDeviceBreakdownDialog } from "../TeamDeviceBreakdownDialog";
import { handleTabListArrowKeys } from "@/lib/tabListKeyboard";

type StoreMetrics = reportingPeriod.StoreMetrics;
type RankedStore = StoreMetrics & { rank: number };

type TableSubView = "volume" | "efficiency" | "extended-rate";
type SubView = TableSubView | "activity";

const SUB_VIEWS: { id: SubView; label: string }[] = [
  { id: "volume", label: "By Volume" },
  { id: "efficiency", label: "By Efficiency" },
  { id: "extended-rate", label: "By Extended Rate" },
  { id: "activity", label: "Daily Activity" },
];

const SUB_VIEW_DESCRIPTIONS: Record<SubView, string> = {
  volume: "Teams ranked by productive hours. The workhorses of the period — who's doing the most.",
  efficiency:
    "Teams ranked by productive hours per device. Normalises for fleet size — who's getting the most out of what they have.",
  "extended-rate":
    "Full per-team picture, ranked by Extended Rate — worst first — so the teams that need attention surface to the top.",
  activity: "Daily recurrence for each team over the selected month — L7 and L28 days active.",
};

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const hoursCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtHours(typeof v === "number" ? v : Number(v))}</span>
);

const pctCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtPct(typeof v === "number" ? v : Number(v))}</span>
);

const dec2Cell = (v: unknown): React.ReactNode => (
  <span className="num">{(typeof v === "number" ? v : Number(v)).toFixed(2)}</span>
);

function makeTeamCell(onSelectTeam: (team: string) => void): (v: unknown) => React.ReactNode {
  return (v) => {
    const name = typeof v === "string" ? v : "";
    return (
      <button
        type="button"
        onClick={() => {
          onSelectTeam(name);
        }}
        className="font-head font-medium text-left hover:text-phoenix-orange hover:underline transition-colors"
      >
        {name}
      </button>
    );
  };
}

const tierCell = (v: unknown): React.ReactNode =>
  typeof v === "string" && v.length > 0 ? <TierBadge tier={v} /> : <span>—</span>;

const rankCell = (v: unknown): React.ReactNode => (
  <span className="num text-muted-foreground">
    #{fmtInt(typeof v === "number" ? v : Number(v))}
  </span>
);

// ── Column sets per table sub-view ───────────────────────────────────────────

function volumeColumns(onSelectTeam: (team: string) => void): DataTableColumn<RankedStore>[] {
  return [
    { key: "rank", label: "Rank", render: rankCell },
    { key: "store", label: "Team", render: makeTeamCell(onSelectTeam) },
    { key: "tier", label: "Tier", render: tierCell, sortable: false },
    { key: "sessions", label: "Sessions", render: numCell },
    { key: "total_hours", label: "Total Hours", render: hoursCell },
    { key: "productive_hours", label: "Productive Hrs", render: hoursCell },
    { key: "devices_total", label: "Devices", render: numCell },
    { key: "productive_hrs_per_device", label: "Prod Hrs/Device", render: dec2Cell },
  ];
}

function efficiencyColumns(onSelectTeam: (team: string) => void): DataTableColumn<RankedStore>[] {
  return [
    { key: "rank", label: "Rank", render: rankCell },
    { key: "store", label: "Team", render: makeTeamCell(onSelectTeam) },
    { key: "tier", label: "Tier", render: tierCell, sortable: false },
    { key: "productive_hrs_per_device", label: "Prod Hrs/Device", render: dec2Cell },
    { key: "sessions_per_device", label: "Sessions/Device", render: dec2Cell },
    { key: "avg_session_hours", label: "Avg Session Hrs", render: hoursCell },
    { key: "devices_total", label: "Devices", render: numCell },
    { key: "unique_users", label: "Unique Users", render: numCell },
  ];
}

function extendedRateColumns(onSelectTeam: (team: string) => void): DataTableColumn<RankedStore>[] {
  return [
    { key: "store", label: "Team", render: makeTeamCell(onSelectTeam) },
    { key: "tier", label: "Tier", render: tierCell, sortable: false },
    { key: "sessions", label: "Sessions", render: numCell },
    { key: "productive_hours", label: "Productive Hrs", render: hoursCell },
    { key: "extended_sessions", label: "Extended Sessions", render: numCell },
    { key: "extended_rate", label: "Extended Rate", render: pctCell },
    { key: "devices_total", label: "Devices", render: numCell },
    { key: "idle_devices", label: "Idle Devices", render: numCell },
    { key: "fleet_idle_rate", label: "Fleet Idle Rate", render: pctCell },
    { key: "unique_users", label: "Unique Users", render: numCell },
  ];
}

// ── Sorting + ranking helpers ────────────────────────────────────────────────

function rankBy(
  stores: StoreMetrics[],
  cmp: (a: StoreMetrics, b: StoreMetrics) => number,
): RankedStore[] {
  return [...stores].sort(cmp).map((s, i) => ({ ...s, rank: i + 1 }));
}

const TABLE_SUB_VIEW_CONFIG: Record<
  TableSubView,
  {
    columns: (onSelectTeam: (team: string) => void) => DataTableColumn<RankedStore>[];
    rank: (stores: StoreMetrics[]) => RankedStore[];
    defaultSort: { key: keyof RankedStore; dir: "asc" | "desc" };
  }
> = {
  volume: {
    columns: volumeColumns,
    rank: (stores) => rankBy(stores, (a, b) => b.productive_hours - a.productive_hours),
    defaultSort: { key: "productive_hours", dir: "desc" },
  },
  efficiency: {
    columns: efficiencyColumns,
    rank: (stores) =>
      rankBy(stores, (a, b) => b.productive_hrs_per_device - a.productive_hrs_per_device),
    defaultSort: { key: "productive_hrs_per_device", dir: "desc" },
  },
  "extended-rate": {
    columns: extendedRateColumns,
    rank: (stores) => rankBy(stores, (a, b) => b.extended_rate - a.extended_rate),
    defaultSort: { key: "extended_rate", dir: "desc" },
  },
};

// ── Pure presentational table component ──────────────────────────────────────

export interface TeamPerformanceTableProps {
  data: reportingPeriod.DashboardData;
  subView: TableSubView;
  onSelectTeam: (team: string) => void;
}

export function TeamPerformanceTable({
  data,
  subView,
  onSelectTeam,
}: TeamPerformanceTableProps): React.JSX.Element {
  const stores = data.methodology.stores;

  if (stores.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-4xl mb-4">📊</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          No teams in this period yet. Upload a reporting period with team data to see this view.
        </p>
      </div>
    );
  }

  const config = TABLE_SUB_VIEW_CONFIG[subView];
  const rows = config.rank(stores);

  return (
    <div className="card-duress p-5">
      <p className="text-xs font-body text-muted-foreground mb-4">
        {SUB_VIEW_DESCRIPTIONS[subView]}
      </p>
      <DataTable<RankedStore>
        key={subView}
        columns={config.columns(onSelectTeam)}
        data={rows}
        defaultSort={config.defaultSort}
        searchKeys={["store"]}
      />
    </div>
  );
}

// ── Smart panels — one per data source ───────────────────────────────────────

function TeamPerformanceTablePanel({
  clientId,
  view,
  subView,
  onSelectTeam,
}: {
  clientId: string;
  view: SelectedView | null;
  subView: TableSubView;
  onSelectTeam: (team: string) => void;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return <LoadingSkeleton variant="table" />;
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return <TeamPerformanceTable data={data} subView={subView} onSelectTeam={onSelectTeam} />;
}

function TeamActivityPanel({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const periodId = view?.mode === "period" ? view.periodId : null;
  const { data, isLoading, error, refetch } = useDailyActivity(clientId, periodId);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (view.mode !== "period") {
    return (
      <EmptyState
        icon={CalendarDays}
        body="Daily activity is scoped to a single month — select one month above (not a custom range or all time) to see this view."
      />
    );
  }

  if (isLoading) {
    return <LoadingSkeleton variant="table" />;
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load daily activity." onRetry={refetch} />;
  }

  return <TeamActivity data={data} />;
}

// ── Smart wrapper that owns sub-view state and dispatches to a panel ────────

export function TeamPerformanceTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const [subView, setSubView] = useState<SubView>("volume");
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Sub-view">
        {SUB_VIEWS.map((v) => {
          const active = v.id === subView;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              data-value={v.id}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => {
                setSubView(v.id);
              }}
              onKeyDown={(e) => { handleTabListArrowKeys(e, (id) => { setSubView(id as SubView); }); }}
              className={`px-3 py-1.5 rounded-full text-xs font-head font-medium border transition-colors
                focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
                ${
                  active
                    ? "bg-phoenix-orange text-white border-phoenix-orange"
                    : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-phoenix-orange"
                }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {subView === "activity" ? (
        <TeamActivityPanel clientId={clientId} view={view} />
      ) : (
        <TeamPerformanceTablePanel
          clientId={clientId}
          view={view}
          subView={subView}
          onSelectTeam={setSelectedTeam}
        />
      )}

      <TeamDeviceBreakdownDialog
        clientId={clientId}
        view={view}
        team={selectedTeam}
        onOpenChange={(open) => {
          if (!open) setSelectedTeam(null);
        }}
      />
    </div>
  );
}
